import { Injectable, inject, signal } from '@angular/core';
import { ApiClientService } from '../core/api/api-client.service';
import { TimeZoneType } from '../shared/models/facility.model';

export interface RoleModel {
  id: number;
  name: string;
}

export interface UserRoleModel {
  id: Guid;
  roleId: number;
  roleName: string;
  scopeType: string | null;
  scopeId: Guid | null;
  scopeName: string | null;
  createdDate: string;
}

export interface UserWithRolesModel {
  userId: Guid;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string | null;
  roles: UserRoleModel[];
}

export interface AssignRoleRequest {
  userId: Guid;
  roleId: number;
  scopeType: string | null;
  scopeId: Guid | null;
}

export interface ChangeRoleRequest {
  userId: Guid;
  currentUserRoleId: Guid | null;
  newRoleId: number | null;
  scopeType: string | null;
  scopeId: Guid | null;
}

export interface ManageableFacilityModel {
  id: Guid;
  name: string;
  organizationId: Guid;
  organizationName: string;
  timeZone: TimeZoneType;
}

export interface ManageableOrganizationModel {
  id: Guid;
  name: string;
}

export interface FacilityEmployeeModel {
  userId: Guid;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string | null;
  roleName: string | null;
  userRoleId: Guid | null;
}

@Injectable({ providedIn: 'root' })
export class RoleManagerService {
  private readonly apiClient = inject(ApiClientService);
  private readonly baseUrl = 'role';

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();

  async getRoles(): Promise<{ roles: RoleModel[] }> {
    return this.apiClient.get<{ roles: RoleModel[] }>(`${this.baseUrl}/list`);
  }

  async getRoleUsers(searchTerm?: string, roleId?: number): Promise<{ users: UserWithRolesModel[] }> {
    this._isLoading.set(true);
    try {
      const params: { [key: string]: any } = {};

      if (searchTerm) {
        params['searchTerm'] = searchTerm;
      }

      if (roleId) {
        params['roleId'] = roleId.toString();
      }

      return await this.apiClient.get<{ users: UserWithRolesModel[] }>(`${this.baseUrl}/users`, params);
    } finally {
      this._isLoading.set(false);
    }
  }

  async getUserRoles(userId: Guid): Promise<{ roles: UserRoleModel[] }> {
    return this.apiClient.get<{ roles: UserRoleModel[] }>(`${this.baseUrl}/user/${userId}`);
  }

  async assignRole(request: AssignRoleRequest): Promise<{
    success: boolean;
    message?: string;
    userRole?: UserRoleModel
  }> {
    return this.apiClient.post<AssignRoleRequest, { success: boolean; message?: string; userRole?: UserRoleModel }>(
      `${this.baseUrl}/assign`,
      request,
    );
  }

  async revokeRole(userRoleId: Guid): Promise<{ success: boolean; message?: string }> {
    return this.apiClient.post<{ userRoleId: Guid }, { success: boolean; message?: string }>(
      `${this.baseUrl}/revoke`,
      { userRoleId },
    );
  }

  async changeRole(
    request: ChangeRoleRequest,
  ): Promise<{ success: boolean; message?: string; newUserRole?: UserRoleModel }> {
    return this.apiClient.post<
      ChangeRoleRequest,
      { success: boolean; message?: string; newUserRole?: UserRoleModel }
    >(`${this.baseUrl}/change`, request);
  }

  async getManageableFacilities(): Promise<{ facilities: ManageableFacilityModel[] }> {
    return this.apiClient.get<{ facilities: ManageableFacilityModel[] }>('role/facilities');
  }

  async getManageableOrganizations(): Promise<{ organizations: ManageableOrganizationModel[] }> {
    return this.apiClient.get<{ organizations: ManageableOrganizationModel[] }>('role/organizations');
  }

  async getFacilityEmployees(facilityId: Guid, searchTerm?: string): Promise<{ employees: FacilityEmployeeModel[] }> {
    this._isLoading.set(true);
    try {
      const params: Record<string, string> = {};

      if (searchTerm) {
        params['searchTerm'] = searchTerm;
      }

      return await this.apiClient.get<{ employees: FacilityEmployeeModel[] }>(
        `role/facility/${facilityId}/employees`,
        params,
      );
    } finally {
      this._isLoading.set(false);
    }
  }
}
